import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './EventChat.css';

function EventChat({ 
  messages, 
  currentUser, 
  loading,
  loadingMore,
  hasMore,
  onSendMessage,
  onLoadMore
}) {
  const { t } = useTranslation(['events', 'shared']);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [newMessage, setNewMessage] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const prevMessagesLengthRef = useRef(0);
  const scrollHeightBeforeLoadRef = useRef(0);

  // Scroll to bottom on initial load or new message
  useEffect(() => {
    if (messages.length === 0) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    // Jeśli to pierwsze ładowanie lub dodano nową wiadomość na koniec
    if (isInitialLoad || messages.length === prevMessagesLengthRef.current + 1) {
      // Scroll to bottom
      messagesEndRef.current?.scrollIntoView({ behavior: isInitialLoad ? 'auto' : 'smooth' });
      setIsInitialLoad(false);
    } else if (messages.length > prevMessagesLengthRef.current) {
      // Załadowano starsze wiadomości - zachowaj pozycję scrolla
      const newScrollHeight = container.scrollHeight;
      const scrollDiff = newScrollHeight - scrollHeightBeforeLoadRef.current;
      container.scrollTop = scrollDiff;
    }

    prevMessagesLengthRef.current = messages.length;
  }, [messages, isInitialLoad]);

  // Detect scroll to top for infinite scroll
  const handleScroll = (e) => {
    const container = e.target;
    
    // Jeśli scrollujemy blisko góry (50px od góry) i mamy więcej wiadomości
    if (container.scrollTop < 50 && hasMore && !loadingMore) {
      scrollHeightBeforeLoadRef.current = container.scrollHeight;
      onLoadMore?.();
    }
  };

  const getUserDisplayName = (user) => {
    if (!user) return t('events.details.unknownUser');
    return user.nickname || user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || t('events.details.unknownUser');
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser) return;

    await onSendMessage(newMessage.trim());
    setNewMessage('');
    
    // Utrzymaj focus na inpucie - poczekaj aż input nie będzie disabled
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  // Helper to check if message should show header (first in group from same user)
  const shouldShowHeader = (msg, index) => {
    if (msg.is_system) return false;
    if (index === 0) return true;
    
    const prevMsg = messages[index - 1];
    if (!prevMsg || prevMsg.is_system) return true;
    if (prevMsg.user_id !== msg.user_id) return true;
    
    // Also show header if more than 5 minutes apart
    const timeDiff = new Date(msg.created_at) - new Date(prevMsg.created_at);
    if (timeDiff > 5 * 60 * 1000) return true;
    
    return false;
  };

  // Check if this is the last message in a group
  const isLastInGroup = (msg, index) => {
    if (msg.is_system) return true;
    if (index === messages.length - 1) return true;
    
    const nextMsg = messages[index + 1];
    if (!nextMsg || nextMsg.is_system) return true;
    if (nextMsg.user_id !== msg.user_id) return true;
    
    const timeDiff = new Date(nextMsg.created_at) - new Date(msg.created_at);
    if (timeDiff > 5 * 60 * 1000) return true;
    
    return false;
  };

  return (
    <div className="event-chat">
      <div className="event-chat-container">
        <div 
          className="event-chat-messages"
          ref={messagesContainerRef}
          onScroll={handleScroll}
        >
          {/* Loading more indicator */}
          {loadingMore && (
            <div className="event-chat-loading-more">
              <span className="event-chat-loading-spinner">⏳</span>
              <span>{t('events.chat.loadingMore', 'Ładowanie...')}</span>
            </div>
          )}
          
          {/* Load more button (optional, for manual loading) */}
          {hasMore && !loadingMore && (
            <button 
              className="event-chat-load-more"
              onClick={() => {
                scrollHeightBeforeLoadRef.current = messagesContainerRef.current?.scrollHeight || 0;
                onLoadMore?.();
              }}
              type="button"
            >
              {t('events.chat.loadMore', 'Załaduj starsze wiadomości')}
            </button>
          )}

          {messages.length === 0 && !loadingMore ? (
            <div className="event-chat-empty">
              <p>{t('events.chat.empty')}</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              // Wiadomość systemowa
              if (msg.is_system) {
                return (
                  <div
                    key={msg.id}
                    className="event-chat-message system"
                  >
                    <div className="event-chat-message-text">
                      {msg.message}
                    </div>
                  </div>
                );
              }

              // Zwykła wiadomość użytkownika
              const isOwn = currentUser && msg.user_id === currentUser.id;
              const showHeader = shouldShowHeader(msg, index);
              const lastInGroup = isLastInGroup(msg, index);
              
              return (
                <div
                  key={msg.id}
                  className={`event-chat-message ${isOwn ? 'own' : ''} ${!showHeader ? 'grouped' : ''} ${lastInGroup ? 'last-in-group' : ''}`}
                >
                  {showHeader && (
                    <div className="event-chat-message-header">
                      <span className="event-chat-message-author">
                        {getUserDisplayName(msg.users)}
                      </span>
                      <span className="event-chat-message-time">
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                  )}
                  <div className="event-chat-message-text">
                    {msg.message}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
        
        <form className="event-chat-input-form" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            className="event-chat-input"
            placeholder={t('events.chat.placeholder')}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            maxLength={500}
            disabled={loading}
          />
          <button
            type="submit"
            className="event-chat-send-button"
            disabled={loading || !newMessage.trim()}
          >
            {t('events.chat.send')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default EventChat;
